import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Switch, Text, useDisclosure } from '@bonusly/design-system';

import { useSeasonalTheme } from './seasonal-theme-context';
import { SeasonalThemeModal } from './seasonal-theme-modal';

export const SeasonalToggle: FC = () => {
	const { t } = useTranslation('components');
	const { hasSeasonalThemePermission, isSeasonalThemeEnabled, toggleTheme } = useSeasonalTheme();
	const { isOpen, onOpen, onClose } = useDisclosure();

	// Only show toggle if user has permission (seasonal-themes feature flag)
	// This is completely independent of the dark-mode-beta feature flag
	if (!hasSeasonalThemePermission) {
		return null;
	}

	const handleToggle = () => {
		if (isSeasonalThemeEnabled) {
			// If enabled, just toggle it off
			toggleTheme();
		} else {
			// If disabled, show the modal first
			onOpen();
		}
	};

	return (
		<>
			<Flex
				alignItems="center"
				gap={2}
				px={3}
				py={1.5}
				borderRadius="full"
				bg={isSeasonalThemeEnabled ? 'primary.bgAlpha' : 'transparent'}
				cursor="pointer"
				onClick={handleToggle}
				transition="all 0.2s"
				_hover={{
					bg: isSeasonalThemeEnabled ? 'primary.bgAlphaHover' : 'secondary.bgAlpha',
				}}
				role="button"
				aria-label={isSeasonalThemeEnabled ? t('seasonal_theme.toggle.disable_label') : t('seasonal_theme.toggle.enable_label')}
			>
				<Text fontSize="sm" fontWeight="medium" color={isSeasonalThemeEnabled ? 'text.default' : 'text.soft'} whiteSpace="nowrap" userSelect="none">
					{t('seasonal_theme.toggle.label', { defaultValue: 'Holiday theme' })}
				</Text>
				<Box onClick={(e) => e.stopPropagation()}>
					<Switch
						size="sm"
						isChecked={isSeasonalThemeEnabled}
						onChange={handleToggle}
						colorScheme="blue"
						aria-label={isSeasonalThemeEnabled ? t('seasonal_theme.toggle.disable_label') : t('seasonal_theme.toggle.enable_label')}
					/>
				</Box>
			</Flex>

			<SeasonalThemeModal isOpen={isOpen} onClose={onClose} onEnable={toggleTheme} />
		</>
	);
};
